/*
  Welcome-offer queue: opens the Klaviyo welcome form only when nothing else is in the way.
  Order: 21+ age gate first, then (2nd page view | 35s on page | 50% scroll), never on top of
  another overlay, never for known Klaviyo profiles, never right after add to cart.
  The Klaviyo form must use "custom trigger" display timing so it only opens from here.
*/
/*
  Auto-apply: when a visitor submits the welcome form, read the code Klaviyo shows on the success
  step and attach it to the Shopify session (discount cookie), so checkout already has it.
  Runs independently of the queue so it also works when Klaviyo opens the form by itself.
*/
(() => {
  const CODE = /\b(LAB15-[A-Z0-9]{7}|WELCOME15)\b/;
  const FORM_ROOTS = '.klaviyo-form, [class*="kl-private-reset"], [data-testid^="klaviyo-form"], [role="dialog"]';
  let applied = false;

  function findCode() {
    for (const node of document.querySelectorAll(FORM_ROOTS)) {
      const match = (node.innerText || '').match(CODE);
      if (match) return match[1];
    }
    return null;
  }

  function confirm(code) {
    const note = document.createElement('div');
    note.className = 'gjl-offer-applied';
    note.setAttribute('role', 'status');
    const header = document.querySelector('.section-header, sticky-header, header');
    const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    note.style.top = `${Math.round(bottom + 8)}px`;
    document.body.appendChild(note);
    // Text goes in on the next frame so screen readers announce the live region.
    requestAnimationFrame(() => { note.textContent = `15% code ${code} is applied. It will show at checkout.`; });
    setTimeout(() => note.remove(), 9000);
  }

  async function apply(code) {
    if (applied) return;
    applied = true;
    try { await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', redirect: 'manual' }); } catch (error) { /* Cookie path failed; cart call below still tries. */ }
    try {
      await fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: code }) });
    } catch (error) { /* Older cart API: the discount cookie alone still applies at checkout. */ }
    try { localStorage.setItem('gjl-offer-code', code); } catch (error) { /* Private mode. */ }
    document.documentElement.setAttribute('data-gjl-offer-code', code);
    document.dispatchEvent(new CustomEvent('gjl:offer-applied', { detail: { code } }));
    confirm(code);
    syncCartDiscount();
  }

  function savedCode() {
    try { const stored = localStorage.getItem('gjl-offer-code'); if (stored) return stored; } catch (error) { /* ignore */ }
    const cookie = document.cookie.split('; ').find(part => part.startsWith('discount_code='));
    return cookie ? decodeURIComponent(cookie.slice(14)) : null;
  }

  let syncing = false;
  async function syncCartDiscount() {
    const code = savedCode();
    if (!code || syncing) return;
    syncing = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const has = (cart.discount_codes || []).some(entry => entry.code && entry.code.toUpperCase() === code.toUpperCase());
      if (cart.item_count > 0 && !has) {
        await fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: code }) });
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('gjl:offer-synced', { detail: { code } }));
      }
    } catch (error) { /* Checkout still applies the discount cookie. */ }
    syncing = false;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncCartDiscount, { once: true });
  else syncCartDiscount();
  document.addEventListener('click', (event) => {
    if (event.target.closest('button[name="add"], .product-form__submit, [data-gjl-sticky-add], [data-gjl-buy-now-proxy], .bndlr-add-to-cart, [class*="add-to-cart"], [class*="AddToCart"]')) {
      setTimeout(syncCartDiscount, 2500);
      setTimeout(syncCartDiscount, 6000);
    }
  }, true);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') syncCartDiscount(); });

  function watchForCode() {
    let tries = 0;
    const timer = setInterval(() => {
      const code = findCode();
      if (code) { clearInterval(timer); apply(code); }
      if (++tries > 60) clearInterval(timer);
    }, 250);
  }

  window.addEventListener('klaviyoForms', (event) => {
    const type = (event.detail || {}).type;
    if (type === 'submit' || type === 'stepSubmit') watchForCode();
  });
})();

(() => {
  const script = document.currentScript;
  const FORM_ID = script?.dataset.formId;
  if (!FORM_ID) return;

  const root = document.documentElement;
  const KEY = 'gjl-offer-state';
  const DISMISS_DAYS = 14;
  const DWELL_MS = 35000;
  const SCROLL_RATIO = 0.5;
  const ATC_QUIET_MS = 10000;
  const BLOCKERS = '#gyj-age-gate, #shopify-pc__banner, #shopify-pc__prefs__dialog, dialog[open], cart-drawer.active, header details[open], shopify-chat[open], #bndlr-discount-message, .menu-drawer-container details[open]';
  const EXCLUDED = /^\/(cart|checkout|account|policies|pages\/contact|pages\/data-sharing-opt-out)/;

  const store = {
    get(area, key) { try { return window[area].getItem(key); } catch (error) { return null; } },
    set(area, key, value) { try { window[area].setItem(key, value); } catch (error) { /* Private mode: skip. */ } }
  };

  const path = location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
  if (EXCLUDED.test(path)) return;

  const saved = (() => { try { return JSON.parse(store.get('localStorage', KEY) || '{}'); } catch (error) { return {}; } })();
  if (saved.submitted) return;
  if (saved.dismissedAt && Date.now() - saved.dismissedAt < DISMISS_DAYS * 864e5) return;
  if (store.get('sessionStorage', 'gjl-offer-shown')) return;

  const views = Number(store.get('sessionStorage', 'gjl-offer-views') || 0) + 1;
  store.set('sessionStorage', 'gjl-offer-views', String(views));

  const isProduct = /^\/products\//.test(path);
  let lastAddToCart = 0;
  let wanted = false;
  let opened = false;
  let timer;

  const visible = (element) => {
    if (!element || !element.isConnected) return false;
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };
  const blocked = () => root.getAttribute('data-gyj-age') !== 'accepted'
    || document.visibilityState !== 'visible'
    || Date.now() - lastAddToCart < ATC_QUIET_MS
    || [...document.querySelectorAll(BLOCKERS)].some(visible);

  // A custom trigger bypasses the form's own visitor targeting, so skip known Klaviyo profiles here.
  function knownFromCookie() {
    try {
      const raw = document.cookie.split('; ').find(part => part.startsWith('__kla_id='));
      if (!raw) return false;
      const data = JSON.parse(atob(decodeURIComponent(raw.slice(9))));
      return Boolean(data.$exchange_id || data.$email || data.$phone_number);
    } catch (error) { return false; }
  }
  function knownSubscriber() {
    if (knownFromCookie()) return Promise.resolve(true);
    if (!window.klaviyo || typeof window.klaviyo.isIdentified !== 'function') return Promise.resolve(false);
    // isIdentified() can stay pending forever on this storefront, so never wait on it for long.
    return Promise.race([
      Promise.resolve(window.klaviyo.isIdentified()).catch(() => false),
      new Promise(resolve => setTimeout(() => resolve(false), 1500))
    ]);
  }

  async function tryOpen() {
    clearTimeout(timer);
    if (opened || !wanted) return;
    if (blocked()) { timer = setTimeout(tryOpen, 2000); return; }
    opened = true;
    if (await knownSubscriber()) return;
    store.set('sessionStorage', 'gjl-offer-shown', '1');
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', FORM_ID]);
  }

  function want() {
    if (wanted) return;
    wanted = true;
    window.removeEventListener('scroll', onScroll);
    tryOpen();
  }

  function onScroll() {
    const max = document.documentElement.scrollHeight - innerHeight;
    if (max > 0 && scrollY / max >= SCROLL_RATIO) want();
  }

  // Track add to cart so the offer never interrupts a purchase.
  document.addEventListener('click', (event) => {
    if (event.target.closest('button[name="add"], .product-form__submit, [data-gjl-sticky-add], [data-gjl-buy-now-proxy]')) lastAddToCart = Date.now();
  }, true);

  // Klaviyo form lifecycle → our state + a root flag other scripts can respect.
  const readState = () => { try { return JSON.parse(store.get('localStorage', KEY) || '{}'); } catch (error) { return {}; } };
  const writeState = (patch) => store.set('localStorage', KEY, JSON.stringify({ ...readState(), ...patch }));
  function settle() {
    // Whoever opened the form (our queue or Klaviyo's own timer), this page is done with it.
    opened = true;
    clearTimeout(timer);
    window.removeEventListener('scroll', onScroll);
    store.set('sessionStorage', 'gjl-offer-shown', '1');
  }

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail || {};
    if (detail.formId && detail.formId !== FORM_ID) return;
    if (detail.type === 'open') { root.setAttribute('data-gjl-offer-open', ''); settle(); }
    if (detail.type === 'close') {
      root.removeAttribute('data-gjl-offer-open');
      settle();
      writeState({ dismissedAt: Date.now() });
    }
    if (detail.type === 'submit' || detail.type === 'stepSubmit') writeState({ submitted: true });
  });

  function arm() {
    window.addEventListener('scroll', onScroll, { passive: true });
    if (isProduct) return; // Product pages: scroll only, never above the fold.
    if (views >= 2) { setTimeout(want, 4000); return; }
    setTimeout(want, DWELL_MS);
  }

  if (root.getAttribute('data-gyj-age') === 'accepted') arm();
  else new MutationObserver((records, observer) => {
    if (root.getAttribute('data-gyj-age') !== 'accepted') return;
    observer.disconnect();
    arm();
  }).observe(root, { attributes: true, attributeFilter: ['data-gyj-age'] });
})();
